export function useCapture() {

  let mirrorCanvas = null
  let mirrorCtx = null
  // Check if ctx.filter actually works (Safari has the property but it's non-functional)
  const canvasFilterSupported = (() => {
    try {
      const c = document.createElement('canvas')
      c.width = 1
      c.height = 1
      const ctx = c.getContext('2d')
      ctx.filter = 'saturate(0)'
      ctx.fillStyle = '#FF0000'
      ctx.fillRect(0, 0, 1, 1)
      const d = ctx.getImageData(0, 0, 1, 1).data
      // If filter worked, red should be desaturated (grayish)
      return d[0] !== 255 || d[1] !== 0
    } catch (e) {
      return false
    }
  })()

  // Manual filter fallback for Safari
  function applyManualFilter(ctx, w, h, filterStr) {
    if (!filterStr || filterStr === 'none') return
    const imgData = ctx.getImageData(0, 0, w, h)
    const d = imgData.data
    const filters = filterStr.match(/[\w-]+\([^)]+\)/g) || []
    for (const f of filters) {
      const match = f.match(/([\w-]+)\(([^)]+)\)/)
      if (!match) continue
      const name = match[1]
      const val = parseFloat(match[2])
      for (let i = 0; i < d.length; i += 4) {
        let r = d[i], g = d[i + 1], b = d[i + 2]
        if (name === 'grayscale') {
          const gray = 0.299 * r + 0.587 * g + 0.114 * b
          r = r + (gray - r) * val
          g = g + (gray - g) * val
          b = b + (gray - b) * val
        } else if (name === 'sepia') {
          const sr = 0.393 * r + 0.769 * g + 0.189 * b
          const sg = 0.349 * r + 0.686 * g + 0.168 * b
          const sb = 0.272 * r + 0.534 * g + 0.131 * b
          r = r + (sr - r) * val
          g = g + (sg - g) * val
          b = b + (sb - b) * val
        } else if (name === 'brightness') {
          r *= val; g *= val; b *= val
        } else if (name === 'contrast') {
          r = (r - 128) * val + 128
          g = (g - 128) * val + 128
          b = (b - 128) * val + 128
        } else if (name === 'saturate') {
          const gray = 0.299 * r + 0.587 * g + 0.114 * b
          r = gray + (r - gray) * val
          g = gray + (g - gray) * val
          b = gray + (b - gray) * val
        } else if (name === 'invert') {
          r = r + (255 - 2 * r) * val
          g = g + (255 - 2 * g) * val
          b = b + (255 - 2 * b) * val
        } else if (name === 'hue-rotate') {
          const deg = val * Math.PI / 180
          const cos = Math.cos(deg), sin = Math.sin(deg)
          const nr = 0.213 + cos * 0.787 - sin * 0.213
          const ng = 0.715 - cos * 0.715 - sin * 0.715
          const nb = 0.072 - cos * 0.072 + sin * 0.928
          const rr = r * nr + g * ng + b * nb
          const gg = r * (0.213 - cos * 0.213 + sin * 0.143) + g * (0.715 + cos * 0.285 + sin * 0.140) + b * (0.072 - cos * 0.072 - sin * 0.283)
          const bb = r * (0.213 - cos * 0.213 - sin * 0.787) + g * (0.715 - cos * 0.715 + sin * 0.715) + b * (0.072 + cos * 0.928 + sin * 0.072)
          r = rr; g = gg; b = bb
        }
        d[i] = Math.max(0, Math.min(255, r))
        d[i + 1] = Math.max(0, Math.min(255, g))
        d[i + 2] = Math.max(0, Math.min(255, b))
      }
    }
    ctx.putImageData(imgData, 0, 0)
  }

  function bilinearInterp(u, v, tl, tr, bl, br) {
    const x = (1 - u) * (1 - v) * tl.x + u * (1 - v) * tr.x + (1 - u) * v * bl.x + u * v * br.x
    const y = (1 - u) * (1 - v) * tl.y + u * (1 - v) * tr.y + (1 - u) * v * bl.y + u * v * br.y
    return { x, y }
  }

  function getMirroredSource(videoEl, cssFilter, effectFn) {
    const vw = videoEl.videoWidth || videoEl.width
    const vh = videoEl.videoHeight || videoEl.height

    // If portrait video (mobile), center-crop to landscape 16:9
    let srcX = 0, srcY = 0, srcW = vw, srcH = vh
    let outW = vw, outH = vh

    if (vh > vw) {
      outW = vw
      outH = Math.round(vw * 9 / 16)
      srcW = vw
      srcH = outH
      srcX = 0
      srcY = Math.round((vh - srcH) / 2)
    }

    if (!mirrorCanvas || mirrorCanvas.width !== outW || mirrorCanvas.height !== outH) {
      mirrorCanvas = document.createElement('canvas')
      mirrorCanvas.width = outW
      mirrorCanvas.height = outH
      mirrorCtx = mirrorCanvas.getContext('2d')
    }
    mirrorCtx.save()
    if (cssFilter && cssFilter !== 'none' && canvasFilterSupported) {
      mirrorCtx.filter = cssFilter
    }
    mirrorCtx.translate(outW, 0)
    mirrorCtx.scale(-1, 1)
    mirrorCtx.drawImage(videoEl, srcX, srcY, srcW, srcH, 0, 0, outW, outH)
    mirrorCtx.restore()
    // Fallback: apply filter manually if ctx.filter not supported
    if (cssFilter && cssFilter !== 'none' && !canvasFilterSupported) {
      applyManualFilter(mirrorCtx, outW, outH, cssFilter)
    }
    if (effectFn) {
      effectFn(mirrorCtx, outW, outH)
    }
    return mirrorCanvas
  }

  function drawPerspective(ctx, videoEl, corners, mirror, cssFilter, effectFn) {
    const source = mirror ? getMirroredSource(videoEl, cssFilter, effectFn) : videoEl
    const videoW = source.width || source.videoWidth
    const videoH = source.height || source.videoHeight
    if (!videoW || !videoH) return

    const { tl, tr, bl, br } = corners
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
    const steps = isMobile ? 20 : 40

    for (let j = 0; j < steps; j++) {
      for (let i = 0; i < steps; i++) {
        const u0 = i / steps
        const v0 = j / steps
        const u1 = (i + 1) / steps
        const v1 = (j + 1) / steps

        // Destination points
        const p0 = bilinearInterp(u0, v0, tl, tr, bl, br)
        const p1 = bilinearInterp(u1, v0, tl, tr, bl, br)
        const p2 = bilinearInterp(u0, v1, tl, tr, bl, br)

        // Source rectangle (straight mapping, mirror already applied)
        const sx = u0 * videoW
        const sy = v0 * videoH
        const sw = (u1 - u0) * videoW
        const sh = (v1 - v0) * videoH

        ctx.save()
        ctx.setTransform(
          (p1.x - p0.x) / sw,
          (p1.y - p0.y) / sw,
          (p2.x - p0.x) / sh,
          (p2.y - p0.y) / sh,
          p0.x,
          p0.y
        )
        ctx.drawImage(source, sx, sy, sw, sh, -1, -1, sw + 2, sh + 2)
        ctx.restore()
      }
    }
  }

  function drawGreenScreen(ctx, config) {
    ctx.save()
    ctx.globalCompositeOperation = 'destination-over'
    ctx.fillStyle = '#00c853'
    if (config.corners) {
      const { tl, tr, bl, br } = config.corners
      ctx.beginPath()
      ctx.moveTo(tl.x, tl.y)
      ctx.lineTo(tr.x, tr.y)
      ctx.lineTo(br.x, br.y)
      ctx.lineTo(bl.x, bl.y)
      ctx.closePath()
      ctx.fill()
    } else {
      ctx.fillRect(config.screen.x, config.screen.y, config.screen.width, config.screen.height)
    }
    ctx.restore()
  }

  function compositeFrame(ctx, videoEl, overlayImg, config, canvasWidth, canvasHeight, hasVideo, cssFilter, effectFn) {
    if (!overlayImg || !config) return

    ctx.clearRect(0, 0, canvasWidth, canvasHeight)

    if (config.corners) {
      const { tl, tr, bl, br } = config.corners

      // 1. Draw the overlay image
      ctx.drawImage(overlayImg, 0, 0, canvasWidth, canvasHeight)

      // 2. Cut out the screen polygon
      ctx.save()
      ctx.beginPath()
      ctx.moveTo(tl.x, tl.y)
      ctx.lineTo(tr.x, tr.y)
      ctx.lineTo(br.x, br.y)
      ctx.lineTo(bl.x, bl.y)
      ctx.closePath()
      ctx.clip()
      ctx.clearRect(0, 0, canvasWidth, canvasHeight)
      ctx.restore()

      // 3. Draw camera feed or green screen behind
      if (hasVideo && videoEl) {
        ctx.save()
        ctx.globalCompositeOperation = 'destination-over'
        drawPerspective(ctx, videoEl, { tl, tr, bl, br }, true, cssFilter, effectFn)
        ctx.restore()
      } else {
        drawGreenScreen(ctx, config)
      }
    } else {
      const { screen } = config

      ctx.drawImage(overlayImg, 0, 0, canvasWidth, canvasHeight)
      ctx.clearRect(screen.x, screen.y, screen.width, screen.height)

      if (hasVideo && videoEl) {
        ctx.save()
        ctx.globalCompositeOperation = 'destination-over'
        ctx.beginPath()
        ctx.rect(screen.x, screen.y, screen.width, screen.height)
        ctx.clip()

        if (cssFilter && cssFilter !== 'none') {
          ctx.filter = cssFilter
        }
        ctx.translate(screen.x + screen.width, screen.y)
        ctx.scale(-1, 1)

        const videoW = videoEl.videoWidth || videoEl.width
        const videoH = videoEl.videoHeight || videoEl.height
        const screenAspect = screen.width / screen.height
        const videoAspect = videoW / videoH

        let sx, sy, sw, sh
        if (videoAspect > screenAspect) {
          sh = videoH
          sw = videoH * screenAspect
          sx = (videoW - sw) / 2
          sy = 0
        } else {
          sw = videoW
          sh = videoW / screenAspect
          sx = 0
          sy = (videoH - sh) / 2
        }

        ctx.drawImage(videoEl, sx, sy, sw, sh, 0, 0, screen.width, screen.height)
        ctx.restore()
      } else {
        drawGreenScreen(ctx, config)
      }
    }
  }

  function capturePhoto(canvasEl) {
    if (!canvasEl) return null
    return canvasEl.toDataURL('image/png')
  }

  function downloadPhoto(dataUrl) {
    if (!dataUrl) return
    const now = new Date()
    const timestamp = now.toISOString().replace(/[:.]/g, '-').slice(0, 19)
    const filename = `laptop-selfie-${timestamp}.png`

    const a = document.createElement('a')
    a.href = dataUrl
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  return {
    compositeFrame,
    downloadPhoto,
    capturePhoto
  }
}
